import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShoppingBag, Palette } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { fmtPrice } from "@/data/catalog";
import { WishlistButton } from "./WishlistButton";

export const ProductCard = ({ p, index = 0 }) => {
  const { t, i18n } = useTranslation();
  const { add } = useCart();

  const onAdd = (e) => {
    e.preventDefault();
    add(p);
    toast.success(t("product.added"), { description: p.name });
  };

  return (
    <Link
      to={`/p/${p.code}`}
      data-testid={`product-card-${p.code}`}
      className="group nv-fade-up relative flex flex-col overflow-hidden rounded-2xl border border-black/[0.06] bg-white transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1 hover:border-black/20 hover:shadow-[0_20px_50px_-20px_rgba(0,0,0,0.25)]"
      style={{ animationDelay: `${Math.min(index, 11) * 40}ms` }}
    >
      <div className="relative aspect-square overflow-hidden bg-[#f6f7f2]">
        <img src={p.img} alt={p.name} loading="lazy" className="nv-card-img h-full w-full object-contain p-6 mix-blend-multiply" />
        {p.tag && (
          <span data-testid="product-tag" className="absolute left-3 top-3 rounded-full bg-black px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#c6ff00]">
            {t(`tags.${p.tag}`)}
          </span>
        )}
        <WishlistButton p={p} className="absolute right-3 top-3 z-10" />
        {p.soldout && (
          <span className="absolute right-14 top-3 rounded-full bg-white/90 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-black/60 backdrop-blur">
            {t("product.soldout")}
          </span>
        )}
        {p.variants && (
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold text-black/70 backdrop-blur">
            <Palette size={12} /> {t("product.variants")}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-4">
        <span className="text-[11px] font-bold uppercase tracking-wider text-black/45">{p.brand}</span>
        <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold leading-snug text-black">{p.name}</h3>
        <div className="mt-auto flex items-center justify-between pt-3">
          <span data-testid="product-price" className="font-display text-lg font-bold">{fmtPrice(p.price, i18n.language)}</span>
          <button
            type="button"
            onClick={onAdd}
            disabled={p.soldout}
            data-testid={`add-to-cart-${p.code}`}
            aria-label={t("product.add")}
            className="grid h-10 w-10 place-items-center rounded-full bg-black text-white transition-[background-color,color,transform] hover:bg-[#c6ff00] hover:text-black active:scale-90 disabled:opacity-30"
          >
            <ShoppingBag size={16} />
          </button>
        </div>
      </div>
    </Link>
  );
};
